import { useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { ClientWithCoreApi } from '@mysten/sui/client'
import { Transaction } from '@mysten/sui/transactions'
import { toBase64 } from '@mysten/sui/utils'
import { Query, QueryKey } from '@tanstack/react-query'
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom'

export const LP_POSITION_CLAIM = 'lp-position-claim'
export const LP_POSITION_COMPOUND = 'lp-position-compound'
export const LP_POSITION_DEPOSIT = 'lp-position-deposit'
export const LP_POSITION_WITHDRAW = 'lp-position-withdraw'
export const LP_NEW_POSITION_CREATE = 'lp-new-position-create'
export const SAV_DEPOSIT = 'sav-deposit'
export const SAV_WITHDRAW = 'sav-withdraw'

export type TransactionType =
  | typeof LP_POSITION_CLAIM
  | typeof LP_POSITION_COMPOUND
  | typeof LP_POSITION_DEPOSIT
  | typeof LP_POSITION_WITHDRAW
  | typeof LP_NEW_POSITION_CREATE
  | typeof SAV_DEPOSIT
  | typeof SAV_WITHDRAW

export const sentryIsEnabled = () =>
  import.meta.env.MODE === 'production' || import.meta.env.MODE === 'staging'

export const initializeSentry = () => {
  if (!sentryIsEnabled()) {
    return
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    tunnel: '/_t/st-9fb5/api/4510414814052432/envelope/',
    sendDefaultPii: true,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.1,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
  })
}

export const initializeSentrySession = (
  walletAddress: string,
  network: string,
  walletName?: string
) => {
  if (!sentryIsEnabled()) {
    return
  }

  Sentry.setUser({
    id: walletAddress,
  })

  Sentry.setTags({
    network,
    ...(walletName !== undefined && { wallet_name: walletName }),
  })
}

const getTxErrorMessage = (
  transactionType: TransactionType,
  operation: 'construct' | 'execute',
  kind?: string
) =>
  `Error ${operation === 'construct' ? 'constructing' : 'executing'} transaction ${transactionType}.${kind ? ` TX failed on-chain: ${kind}.` : ''}`

export const getTxError = (
  transactionType: TransactionType,
  operation: 'construct' | 'execute',
  kind?: string
) => new Error(getTxErrorMessage(transactionType, operation, kind))

export const logTxError = (
  transactionType: TransactionType,
  error: unknown,
  context: Record<string, unknown> = {}
) => {
  if (!sentryIsEnabled()) {
    return
  }

  Sentry.captureException(error, {
    tags: {
      transaction_type: transactionType,
    },
    extra: {
      ...context,
    },
  })
}

export const logRequestError = (
  error: Error,
  query: Query<unknown, unknown, unknown, QueryKey>
) => {
  if (!sentryIsEnabled()) {
    return
  }

  Sentry.captureException(error, {
    tags: {
      source: 'react-query',
      kind: 'query',
    },
    extra: {
      queryKey: query.queryKey,
      meta: query.meta,
    },
  })
}

export const logErrorMessage = (message: string, context: Record<string, unknown> = {}) => {
  if (!sentryIsEnabled()) {
    return
  }

  Sentry.captureException(new Error(message), {
    extra: context,
  })
}

export const getTxData = async (tx: Transaction, client: ClientWithCoreApi) => {
  try {
    const buildedTx = await tx.build({ client, onlyTransactionKind: true })
    const txData = toBase64(buildedTx)
    return { txBytes: txData }
  } catch {
    const txJsonData = await tx.toJSON()
    return { txDataJson: JSON.stringify(txJsonData) }
  }
}

export const clearSentrySession = () => {
  Sentry.setUser(null)
  Sentry.setTag('network', null)
  Sentry.setTag('walletName', null)
}
